/**
 * Direct-challenge types shared between client and server.
 */

import type { TimeControlKey } from './time-control.js';
import type { GameMode, OpeningMode } from './game-mode.js';
import type { MatchLegs } from './match.js';

/**
 * Which side the challenger chooses to play. `'random'` is resolved to an actual
 * side (white/black) by the server when the challenge is accepted.
 */
export type ChallengeColor = 'white' | 'black' | 'random';

/** Selectable colors, ordered as shown in the challenge dropdown (random first). */
export const CHALLENGE_COLORS: readonly ChallengeColor[] = ['random', 'white', 'black'] as const;

/** Default color when none is chosen — a fair random side. */
export const DEFAULT_CHALLENGE_COLOR: ChallengeColor = 'random';

/**
 * Who is allowed in, and whether the result counts.
 *
 * The old boolean answered both questions with one bit, and that made "this game
 * counts" and "this table is open to everyone" the same choice: the only way to
 * keep guests and bots out of a table was… nothing, they always got in and the
 * game quietly downgraded itself.
 *
 *  - `'any'`      everyone may sit; the game is rated only if both players are
 *                 registered humans. This is EXACTLY what `rated: true` has
 *                 always meant in practice, which is why it is the default.
 *  - `'rated'`    registered humans only — no guests, no bots. The one type that
 *                 can promise up front that the result will count.
 *  - `'casual'`   everyone may sit and nothing is ever rated.
 *
 * `rated` is kept alongside and derives from this (`matchType !== 'casual'`), so
 * everything downstream of `normalizeChallengeSettings` — up to `room.isRated` —
 * keeps reading the single flag it already reads.
 *
 * ── `'any'` is a policy of the DOOR ─────────────────────────────────────────
 *
 * Which means it only says something while the door is open: a public table, a
 * link waiting to be pasted into WhatsApp. Once the guest list is one name long
 * — a direct challenge — "everyone may sit, and it counts if it can" stops being
 * a type of game and becomes a question whose answer is already known.
 * {@link resolveMatchType} is what it turns into there.
 */
export type MatchType = 'any' | 'rated' | 'casual';

/** Selectable match types, ordered as shown in the dropdown. */
export const MATCH_TYPES = ['any', 'rated', 'casual'] as const satisfies readonly MatchType[];

/** Default when none is chosen — open to everyone, counts when it can. */
export const DEFAULT_MATCH_TYPE: MatchType = 'any';

export function isMatchType(value: unknown): value is MatchType {
  return typeof value === 'string' && (MATCH_TYPES as readonly string[]).includes(value);
}

/**
 * A player, reduced to the only thing a match type asks about: is there a rating
 * to move? Guests and bots have none (see the server's `hasRating`).
 */
export interface MatchTypeParticipant {
  isGuest?: boolean;
  isBot?: boolean;
}

/**
 * Whether this player may enter a game of this type. A player who cannot move
 * ratings cannot enter a game that promises to move them.
 */
export function canJoinMatchType(
  matchType: MatchType,
  player: MatchTypeParticipant
): boolean {
  return matchType !== 'rated' || (!player.isGuest && !player.isBot);
}

/**
 * The match type said once it is known WHO plays.
 *
 * `'any'` is a door policy (see {@link MatchType}), so it resolves the moment the
 * door is shut on a known set of players: rated if every one of them can move a
 * rating, casual otherwise.
 *
 * ⚠️ `'rated'` is NOT downgraded, deliberately. It is the one type the door uses
 * to turn people away (`REGISTERED_ONLY`), and calling a game "casual" when it is
 * about to be refused would be a lie about what happens next. Saying no is
 * `canJoinMatchType`'s job, not this one's — which is why a caller that resolves
 * for a guest must ALSO force casual where it builds the payload.
 *
 * The first player is mandatory on purpose: with nobody at all `every` returns
 * `true`, and a challenge to an unknown recipient would come out "rated". Code
 * that does not know who plays must not be able to call this.
 */
export function resolveMatchType(
  matchType: MatchType,
  player: MatchTypeParticipant,
  ...others: MatchTypeParticipant[]
): Exclude<MatchType, 'any'> {
  if (matchType !== 'any') return matchType;
  return [player, ...others].every((p) => canJoinMatchType('rated', p)) ? 'rated' : 'casual';
}

/**
 * Game settings chosen in the direct-challenge modal and sent with `challenge:send`.
 * Kept loose on `variant`/`timeControl` (plain strings on the wire) to match the
 * modal's local state; the server narrows them.
 */
export interface ChallengeSettings {
  variant: string;
  /** Any cadence the two players agree on, or `'none'` (∞). Server re-validates. */
  timeControl: TimeControlKey;
  /** Side the challenger wants; `'random'` is resolved server-side on accept. */
  color: ChallengeColor;
  /** Who may play, and whether it counts. See {@link MatchType}. */
  matchType: MatchType;
  /**
   * Whether the game affects Glicko-2 ratings (false = casual/amichevole).
   * Derived from `matchType` server-side; still on the wire so a client on a
   * cached bundle keeps being understood.
   */
  rated: boolean;
  /** Win-condition modifier (normal / breakthrough / antidraughts). Any mode can be rated. */
  gameMode: GameMode;
  /** Opening selection (free / drawn over 2 or 3 half-moves). */
  opening: OpeningMode;
  /**
   * Games the challenge is played over: 2 = andata e ritorno (colours swapped
   * between them), 1 = solo andata. Offered for every opening; with a drawn one
   * the two legs replay the SAME draw and are rated together, with a free one
   * they are two ordinary games rated on their own. See {@link MatchLegs}.
   */
  legs: MatchLegs;
  /**
   * Optional custom starting position (Polish-notation FEN, e.g. `W:W31,32,K10:B1,2`),
   * set when the challenge originates from the board EDITOR. When present the game
   * starts from this position instead of the standard opening; such games are always
   * casual/unrated and ignore `opening` (a drawn opening on a composed board is
   * meaningless). The server re-validates the FEN and forces `rated=false`,
   * `opening='free'` and `legs=1`.
   */
  position?: string;
}

/**
 * How long a shareable challenge link stays valid (1 hour).
 *
 * Much longer than the 60s of a direct challenge: the link is meant to be pasted
 * into WhatsApp and answered whenever the friend picks up the phone. The
 * challenger does NOT have to stay connected in the meantime — the friend
 * "claims" the link and the game starts as soon as both are online again.
 */
export const LINK_CHALLENGE_TTL_MS = 60 * 60 * 1000;

/** Lifecycle of a shareable challenge link. */
export type LinkChallengeStatus = 'open' | 'claimed' | 'started' | 'cancelled' | 'expired';

/**
 * Public view of a challenge link, as returned by `GET /api/challenges/link/:id`
 * and by the `challenge:create-link` ack. Deliberately carries no user ids —
 * anyone holding the link can read this, logged in or not.
 */
export interface LinkChallengeInfo {
  id: string;
  /** Display name of whoever created the link. */
  fromUsername: string;
  /** `null` for an account with no rating. Only registered humans own one. */
  fromRating: number | null;
  variant: string;
  timeControl: TimeControlKey;
  color: ChallengeColor;
  /** Who may accept, and whether it counts. See {@link MatchType}. */
  matchType: MatchType;
  rated: boolean;
  gameMode: GameMode;
  opening: OpeningMode;
  /** 2 = andata e ritorno, 1 = solo andata. Offered for every opening. */
  legs: MatchLegs;
  /** True when the game starts from a composed position (board editor). */
  hasPosition: boolean;
  status: LinkChallengeStatus;
  /** Epoch ms; the client shows a countdown and stops offering "accept" past it. */
  expiresAt: number;
  /** Display name of the friend who claimed the link, once claimed. */
  claimedByUsername?: string;
}
